/* @jsx h */
import { defineComponent, h } from "vue";
import { RouterLink } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";

type Props = {
  label: string;
  value: string;
  hint?: string | null;
  /** % change vs the previous period. */
  change?: number | null;
  /** Whether a rise is good (stock value) or bad (food cost, waste). */
  riseIsBad?: boolean;
  icon: string;
  to?: string;
};

// One headline number with its trend.
export const KpiTile = defineComponent<Props>({
  name: "KpiTile",
  props: ["label", "value", "hint", "change", "riseIsBad", "icon", "to"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const c = props.change;
      const bad = c != null && (props.riseIsBad ? c > 0.5 : c < -0.5);
      const good = c != null && (props.riseIsBad ? c < -0.5 : c > 0.5);
      const body = (
        <div class="flex h-full flex-col rounded-xl border border-base-300 bg-base-100 p-4 transition hover:border-base-content/20">
          <div class="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-base-content/50">
            {props.label}
            <span class="material-symbols-outlined text-base text-base-content/30">{props.icon}</span>
          </div>
          <div class="mt-1 text-2xl font-semibold tabular-nums text-accent">{props.value}</div>
          <div class="mt-auto flex items-center gap-2 pt-1 text-xs text-base-content/50">
            {c != null && (
              <span
                class={["inline-flex items-center font-medium", bad ? "text-error" : good ? "text-success" : ""].join(" ")}
                title={t("inventory.overview.vs_previous")}
              >
                <span class="material-symbols-outlined text-sm">{c >= 0 ? "arrow_upward" : "arrow_downward"}</span>
                {`${Math.abs(c).toFixed(0)}%`}
              </span>
            )}
            {props.hint && <span class="truncate">{props.hint}</span>}
          </div>
        </div>
      );
      return props.to ? <RouterLink to={props.to} class="block">{body}</RouterLink> : body;
    };
  },
});
